(ns machtfit.stepchall.add-activity
  (:require [cljs-time.core :as time]
            [clojure.string :as str]
            [cognitect.transit :as transit]
            [machtfit.app.routes :as routes]
            [machtfit.core :as machtfit]
            [machtfit.format :as format]
            [machtfit.human :as human]
            [machtfit.startpage.api-events :as api-events]
            [machtfit.stepchall.api :as api]
            [machtfit.stepchall.countdown :as countdown]
            [machtfit.stepchall.date :as date]
            [machtfit.stepchall.icons :as icons]
            [machtfit.stepchall.modal :as modal]
            [machtfit.stepchall.subs :as subs]
            [machtfit.translation :refer [tr tr-with-place-holders t trp-with-place-holders format-date]]
            [re-frame.core :as rf]))

(def ^:const factors-threshold 10000)

(rf/reg-event-fx ::navigate-to-stepchall-select-activity
  (fn [{:keys [db]} [_ page]]
    (-> {:db db}
        (assoc-in [:db :ui :page] page)
        (assoc :scroll-to {:frame :app-frame
                           :mode :page-top})
        (api-events/api-requests-on-stepchall-event))))

(defn- date->weekday [date]
  (-> date
      time/day-of-week
      human/weekday
      :long
      t))

(defn- fair-play-countdown []
  (let [current-time @(rf/subscribe [:get [:clock :current-server-time]])
        midnight @(rf/subscribe [:get [:server-info :reference-tomorrow-start]])
        seconds-until-midnight (- midnight current-time)
        today @(rf/subscribe [:get [:server-info :reference-today]])
        entry-date @(rf/subscribe [::date/entry-date])
        less-than-two-hours-left-for-entry-date? (and (= (time/minus today (time/days 2)) entry-date)
                                                      (<= seconds-until-midnight 7200))]
    (when less-than-two-hours-left-for-entry-date?
      [:div.day-reference-alert
       [:b
        (tr-with-place-holders
         "Du hast noch {countdown} um {main-units} für {weekday} hinzuzufügen."
         {"countdown" (countdown/countdown {:from current-time
                                            :until midnight})
          "weekday" (tr (date->weekday @(rf/subscribe [::date/entry-date])))
          "main-units" (t @(rf/subscribe [:get [:stepchall :main-unit :plural]]))})]
       [:br]
       [:span (tr "Die 3-Tage-Fair-Play Regel setzt nach Mitternacht ein.")]])))

(defn- activity-header []
  (let [entry-date @(rf/subscribe [::date/entry-date])]
    [:div
     [:h2 {:style {:margin-bottom "0"}}
      (tr (date->weekday entry-date))]
     [:p (format-date entry-date)]]))

(rf/reg-sub ::activities-sorted-by-name
  :<- [:get [:stepchall :activities]]
  :<- [:get [:language]]

  (fn [[activities language] _]
    (into (sorted-map-by (fn [key1 key2]
                           (compare
                            (str/lower-case (get-in activities [key1 :name language]))
                            (str/lower-case (get-in activities [key2 :name language])))))
          activities)))

(defn- activity-list []
  (let [entry-date @(rf/subscribe [::date/entry-date])
        main-unit-plural @(rf/subscribe [:get [:stepchall :main-unit :plural]])
        activities @(rf/subscribe [::activities-sorted-by-name])
        unsorted-activities @(rf/subscribe [:get [:stepchall :activities]])
        default-activity-exists? (get unsorted-activities :steps)]
    [:div
     (when default-activity-exists?
       [:a.sc_btn.sc_btn-lg.sc_btn-white (routes/attrs-link-to-page {:id :stepchall
                                                                     :subpage :add-activity
                                                                     :entry-date entry-date
                                                                     :activity :steps})
        (trp-with-place-holders {:de "{main-unit-plural} hinzufügen"
                                 :en "Add {main-unit-plural}"}
                                {"main-unit-plural" (t main-unit-plural)})])
     (when (seq activities)
       [:<>
        (if default-activity-exists?
          [:<>
           [:div.sc_separator [:span (tr "oder")]]
           [:h3 (tr "Möchtest du eine andere Aktivität hinzufügen?")]]
          [:h3 (tr "Welche Aktivität möchtest du hinzufügen?")])
        [:div.sc_activity-list
         (doall (for [[activity activity-data] activities
                      :let [{:keys [name]} activity-data]]
                  ^{:key name}
                  [:a.sc_activity-list-item (routes/attrs-link-to-page {:id :stepchall
                                                                        :subpage :add-activity
                                                                        :entry-date entry-date
                                                                        :activity activity})
                   (t name)]))]])]))

(rf/reg-event-db ::activity-value-changed
  (fn [db [_ value]]
    (assoc-in db [:ui :stepchall :activity-value] (int value))))

(defn- activity-value-input [activity]
  (let [activity-data @(rf/subscribe [::subs/activity-data activity])]
    [:div.sc_activity-input.sc_input-group
     [:div
      [:input.sc_input {:type "number"
                        :value @(rf/subscribe [:get [:ui :stepchall :activity-value]])
                        :auto-focus true
                        :on-change #(rf/dispatch-sync [::activity-value-changed (-> % .-target .-value)])}]]
     [:label.sc_input-label (t @(rf/subscribe [::subs/unit-name (:unit activity-data)]))]]))

(rf/reg-event-db
  ::show-activity-confirmation-modal
  (fn [db [_ activity]]
    (assoc-in db [:ui :stepchall :add-activity :confirmation-modal] activity)))

(rf/reg-event-db
  ::unset-activity-confirmation-modal
  (fn [db _]
    (assoc-in db [:ui :stepchall :add-activity] nil)))

(rf/reg-event-fx ::report-activity
  (fn [{:keys [db]} _]
    (let [date (-> db
                   date/entry-date
                   format/date->name)]
      {:post (api/request db {:function :stepchall-report-activity
                              :params {:activity (-> db :ui :page :activity name)
                                       :activity_date (transit/tagged-value "D" date)
                                       :count (-> db :ui :stepchall :activity-value)}
                              :page-on-success {:id :stepchall
                                                :subpage :my-steps}})})))

(rf/reg-event-fx
  ::report-activity-and-unset-modal
  (fn [_ _]
    {:dispatch-n [[::unset-activity-confirmation-modal]
                  [::report-activity]]}))

(rf/reg-event-fx ::add-activity-clicked
  (fn [{:keys [db]} _]
    (let [activity-key (-> db :ui :page :activity)
          activity-count (-> db :ui :stepchall :activity-value)
          activities (-> db :stepchall :activities)
          activity (-> activities activity-key)
          activity-unit (:unit activity)
          activity-factor (-> activity :factor)]
      (if (>= (* activity-factor activity-count) factors-threshold)
        {:dispatch [::show-activity-confirmation-modal
                    (cond-> (assoc activity :count activity-count)
                      (= activity-unit :steps) (dissoc :name))]}
        {:dispatch [::report-activity]}))))

(defn- activity-value []
  (let [activity @(rf/subscribe [:get [:ui :page :activity]])
        activity-data @(rf/subscribe [::subs/activity-data activity])
        add-activity-request-in-progress? @(rf/subscribe [:get [:ui :page :add-activity-request-in-progress?]])
        add-activity-request-in-progress-from-modal? @(rf/subscribe [:get [:ui :page :add-activity-request-in-progress-from-modal?]])
        confirmation-modal @(rf/subscribe [:get [:ui :stepchall :add-activity :confirmation-modal]])]
    [:div
     [:div.sc_content-top
      (if (= :steps activity)
        [:p (tr-with-place-holders "Wieviele {main-units} möchtest du hinzufügen?"
                                   {"main-units" (t @(rf/subscribe [:get [:stepchall :main-unit :plural]]))})]
        [:p (tr-with-place-holders
             "Wieviele {unit-name} möchtest du für die Aktivität {activity} eintragen?"
             {"unit-name" (t @(rf/subscribe [::subs/unit-name (:unit activity-data)]))
              "activity" (t (:name activity-data))})])

      [activity-value-input activity]]
     [:button.sc_btn.sc_btn-lg.sc_btn-white-ghost {:disabled add-activity-request-in-progress?
                                                   :on-click (when-not add-activity-request-in-progress?
                                                               #(do
                                                                  (rf/dispatch-sync [:set-path [:ui :page :add-activity-request-in-progress?] true])
                                                                  (rf/dispatch [::add-activity-clicked])))}
      (str (tr "Aktivität hinzufügen")
           (when add-activity-request-in-progress?
             "..."))]
     (when confirmation-modal
       [modal/modal {:title (t {:de "Neue Aktivität hinzufügen"
                                :en "Add new activity"})
                     :content [:p
                               (trp-with-place-holders
                                {:de "Möchtest du {activity-count} {activity-unit} {activity-name} hinzufügen?"
                                 :en "Would you like to add {activity-count} {activity-unit} {activity-name}?"}
                                {"activity-count" (format/decimal (:count confirmation-modal))
                                 "activity-unit" (t @(rf/subscribe [::subs/unit-name (:unit confirmation-modal)]))
                                 "activity-name" (if-let [activity-name (:name confirmation-modal)]
                                                   (str (t {:de ""
                                                            :en " of "})
                                                        (t activity-name))
                                                   "")})]
                     :modal-style "text-black"
                     :button-group [{:text (tr "Weiter")
                                     :on-click (when-not add-activity-request-in-progress-from-modal?
                                                 #(do
                                                    (rf/dispatch-sync [:set-path [:ui :page :add-activity-request-in-progress-from-modal?] true])
                                                    (rf/dispatch [::report-activity-and-unset-modal])))}]
                     :close-button {:on-click (when-not add-activity-request-in-progress-from-modal?
                                                #(do
                                                   (rf/dispatch-sync [:set-path [:ui :page :add-activity-request-in-progress-from-modal?] false])
                                                   (rf/dispatch-sync [:set-path [:ui :page :add-activity-request-in-progress?] false])
                                                   (rf/dispatch [::unset-activity-confirmation-modal])))}}])]))

(rf/reg-sub ::day-out-of-fair-play?
  :<- [::date/entry-date]
  :<- [:get [:server-info :reference-today]]

  (fn [[entry-date today] _]
    (-> today
        (time/minus (time/days 2))
        (> entry-date))))

(defn- out-of-fair-play-modal []
  (when @(rf/subscribe [::day-out-of-fair-play?])
    [modal/modal {:img-src (machtfit/static "img/stepchall/time-out.svg")
                  :title (tr "Es tut uns Leid!")
                  :content [(fn []
                              (let [entry-date @(rf/subscribe [::date/entry-date])
                                    weekday (date->weekday entry-date)]
                                [:p
                                 (tr-with-place-holders
                                  "Deine {main-units} für {weekday} ({date}) werden nicht mitgezählt, weil die Frist für das Eintragen der {main-units} abgelaufen ist."
                                  {"main-units" (t @(rf/subscribe [:get [:stepchall :main-unit :plural]]))
                                   "weekday" (tr weekday)
                                   "date" (format-date entry-date)})]))]
                  :button-group [{:text (tr "Weiter")
                                  :page {:id :stepchall
                                         :subpage :my-steps}}]
                  :close-button {:page {:id :stepchall
                                        :subpage :my-steps}}
                  :help-attr {:question :steps-3-days-fair-play}}]))

(defn- too-far-in-the-past-error-modal []
  (when (= @(rf/subscribe [:get [:ui :page :error :name]]) :too-far-in-the-past-edit)
    [modal/modal {:img-src (machtfit/static "img/stepchall/late-arrival.svg")
                  :title "Das war so knapp."
                  :content [(fn []
                              (let [entry-date @(rf/subscribe [::date/entry-date])
                                    weekday (date->weekday entry-date)]
                                [:p (tr-with-place-holders
                                     "Deine {main-units} für {weekday} ({date}) werden nicht mitgezählt, da sie erst nach 0 Uhr  eingegangen sind."
                                     {"main-units" (t @(rf/subscribe [:get [:stepchall :main-unit :plural]]))
                                      "weekday" (tr weekday)
                                      "date" (format-date entry-date)})]))]
                  :button-group [{:text (tr "Weiter")
                                  :page {:id :stepchall
                                         :subpage :my-steps}}]
                  :close-button {:page {:id :stepchall
                                        :subpage :my-steps}}
                  :help-attr {:question :steps-3-days-fair-play}}]))

(defn- activity-page []
  (case @(rf/subscribe [:get [:ui :page :subpage]])
    :select-activity [activity-list]
    :add-activity [activity-value @(rf/subscribe [:get [:ui :page :activity]])]))

(defn activities []
  [:<>
   [:div.sc_add-activity
    [:div.sc_container
     [:div.sc_back [:span {:on-click #(js/history.back)} [:i.sc_icon [icons/back]] (tr "zurück")]]
     [fair-play-countdown]
     [activity-header]
     [activity-page]]]
   [out-of-fair-play-modal]
   [too-far-in-the-past-error-modal]])
